import { useCallback, useMemo } from "react";
import { AiOutlineSelect } from "react-icons/ai";
import { MdEdit, MdGroup, MdStar } from "react-icons/md";
import { RiLogoutBoxRLine, RiSuitcaseFill, RiTrophyFill } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import LanguageSelection from "../../../components/language-selection";
import useRootTranslation from "../../../hooks/use-root-translation";
import { OriginType } from "../../../models/track-type.enum";
import { AppState } from "../../../store/app-state";
import { logout } from "../../../store/auth/auth-actions";
import SidebarAcordeon from "./components/sidebar-acordeon";
import SidebarItem, { SidebarItemProps } from "./components/sidebar-item";
import { SideBarProps } from "./sidebar.models";
import {
  SideBarContent,
  SideBarLeftContainer,
  TalentLogo,
} from "./sidebar.styled";

const SideBarLeft: React.FC<SideBarProps> = ({ isOpen, setIsOpen }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { root } = useRootTranslation("side-left", "private-home-page");
  const { user } = useSelector((state: AppState) => state.authState);
  const { data: trackings } = useSelector(
    (state: AppState) => state.eventState.eventTracksData
  );

  const handleLogout = useCallback(() => {
    dispatch(logout());
  }, [dispatch]);

  const closeOnClick = useCallback(
    (func) => {
      setIsOpen(false);
      func();
    },
    [setIsOpen]
  );

  const trackToItem = useCallback(
    (track): SidebarItemProps => ({
      id: track.uuid,
      icon: MdEdit,
      label: track.name,
      onClick: () => closeOnClick(() => history.push(`/track/${track.uuid}`)),
    }),
    [history, closeOnClick]
  );

  const jobItens: SidebarItemProps[] = useMemo(() => {
    const jobTrack: SidebarItemProps[] =
      trackings
        ?.filter((t) => t.origin_type === OriginType.JOB)
        .map(trackToItem) || [];
    return [
      ...jobTrack,
    ];
  }, [trackings, trackToItem]);

  const trackItens: SidebarItemProps[] = useMemo(() => {
    return (
      trackings
        ?.filter((t) => t.origin_type !== OriginType.JOB)
        .map(trackToItem) || []
    );
  }, [trackings, trackToItem]);

  const isSelected = useCallback(
    (path: string): boolean => {
      return history.location.pathname.includes(path);
    },
    [history]
  );

  return (
    <SideBarLeftContainer
      isOpen={isOpen}
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <div>
        <TalentLogo
          src={
            isOpen
              ? "https://talent.dio.me/assets/img/talent-match-onboarding.svg"
              : "/assets/img/dio-icon.svg"
          }
        />
        <SideBarContent>
          <SidebarItem
            id="selecteds"
            icon={AiOutlineSelect}
            label={root("selecteds")}
            onClick={() =>
              closeOnClick(() => history.push("/selected-candidates"))
            }
            showFull={isOpen}
            selected={isSelected("/selected-candidates")}
          />
          <SidebarItem
            id="ranking"
            icon={MdStar}
            label={root("ranking")}
            onClick={() => closeOnClick(() => history.push("/ranking"))}
            showFull={isOpen}
            selected={isSelected("/ranking")}
          />
          <SidebarAcordeon
            id="job-section"
            icon={RiSuitcaseFill}
            label={root("job-section")}
            itens={jobItens}
            sidebarIsOpen={isOpen}
            setSidebarIsOpen={setIsOpen}
            selected={false}
          />
          <SidebarAcordeon
            id="track-section"
            icon={RiTrophyFill}
            label={root("track-section")}
            itens={trackItens}
            sidebarIsOpen={isOpen}
            setSidebarIsOpen={setIsOpen}
            selected={isSelected("/track")}
          />
        </SideBarContent>
      </div>
      <div
        id="spacer"
        style={{
          height: "100%",
        }}
        onClick={() => !isOpen && setIsOpen(!isOpen)}
      />
      <div>
        <LanguageSelection showAllLanguages={isOpen} />
        {user?.is_talent_admin && (
          <SidebarItem
            id="profile"
            icon={MdGroup}
            label={root("profile")}
            onClick={() => closeOnClick(() => history.push("/company-profile"))}
            showFull={isOpen}
          />
        )}
        <SidebarItem
          id="logout"
          icon={RiLogoutBoxRLine}
          label={root("logout")}
          onClick={handleLogout}
          showFull={isOpen}
        />
      </div>
    </SideBarLeftContainer>
  );
};

export default SideBarLeft;
